import { Add as AddIcon } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { parse } from 'papaparse';
import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';

import { PrimaryButton } from 'src/components/Buttons/PrimaryButton';
import { Show } from 'src/components/Show';
import { colors } from 'src/global/styles/colors';
import { ProjectInputs, ProjectTierLayer } from 'src/global/types/project.type';
import { useDeployment } from 'src/provider/DeploymentProvider';
import { parseConfig, validateCsv } from 'src/utils/template';

import { DownloadButton } from '../DownloadButton';
import { UploadButton } from '../UploadButton';
import AutoPriceAdjustment from './AutoPriceAdjustment';
import { TierItem } from './TierItem';

export function TierPricing({ isDisabled = false }: { isDisabled?: boolean }) {
  const { hasDeployed } = useDeployment();
  const [expandRows, setExpandRows] = useState<string[]>([]);
  const [isImporting, setIsImporting] = useState(false);

  const {
    control,
    trigger,
    clearErrors,
    formState: { errors },
  } = useFormContext<ProjectInputs>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'tier.layer',
  });

  const prevFieldsRef = useRef(fields);

  useEffect(() => {
    if (!fields.length || isImporting) return;
    const currentIds = fields.map(field => field.id);
    const previousIds = prevFieldsRef.current.map(field => field.id);

    prevFieldsRef.current = fields;

    const addedIds = currentIds.filter(id => !previousIds.includes(id));
    if (addedIds.length === 1) {
      // Only expand for single additions
      setExpandRows(prev => [...prev, ...addedIds]);
    }

    const removedIds = previousIds.filter(id => !currentIds.includes(id));
    if (removedIds.length > 0) {
      setExpandRows(prev => prev.filter(id => !removedIds.includes(id)));
    }
  }, [fields, isImporting]);

  const handleUploadCsvFile = (files: FileList) => {
    const file = files[0];
    if (!file) {
      toast.error('No file selected');
      return;
    }

    setIsImporting(true);
    const reader = new FileReader();

    reader.onload = async e => {
      try {
        if (!e.target?.result) {
          toast.error('Error reading file');
          return;
        }

        const csv = e.target.result as string;
        const validationRes = validateCsv(csv, {
          headers: ['CAPABILITY', 'PRICE'],
          columnTypes: ['number', 'number'],
        });

        if (!validationRes.isValid) {
          toast.error(validationRes.errors[0]);
          return;
        }

        const parseResult = parse(csv, parseConfig);
        const tiers = parseResult.data
          .slice(1)
          .filter((row: any) => row.length >= 2 && !isNaN(Number(row[0])) && !isNaN(Number(row[1])))
          .map((row: any) => ({
            total: Number(row[0]),
            price: Number(row[1]),
          }))
          .filter(tier => tier.total > 0 && tier.price >= 0);

        if (tiers.length === 0) {
          toast.error('No valid tiers found in CSV');
          return;
        }

        remove();
        append(tiers as ProjectTierLayer[]);
        setExpandRows([]);
        toast.success('Tiers imported successfully');
      } catch (error) {
        console.error('Error processing CSV:', error);
        toast.error('Error processing CSV file');
      } finally {
        setIsImporting(false);
      }
    };

    reader.onerror = () => {
      toast.error('Error reading the CSV file');
      setIsImporting(false);
    };

    reader.readAsText(file);
  };

  return (
    <Box>
      <Typography variant='body1' fontSize={'14px'} fontWeight={400} sx={{ color: 'white' }} paddingBottom={3}>
        Learn how Tiered Sales work, including supply caps, automatic price increases, and price limits. Visit the{' '}
        <a
          href={'https://link.lfg.inc/kb-coresale-pricingmodel'}
          target={'_blank'}
          style={{ color: 'white', textDecoration: 'underline' }}>
          Tiered Pricing Model
        </a>{' '}
        article for more details.
      </Typography>
      <Typography fontSize={'16px'} color='white' lineHeight={'20px'} paddingBottom={2} fontWeight={500} variant='h6'>
        Tiered Sale
      </Typography>
      <Typography fontSize={'14px'} color='white' lineHeight={'20px'} variant='h6' paddingBottom={3}>
        Tier automatically advances when current tier sold out.
      </Typography>
      <Show when={!hasDeployed}>
        <Stack
          spacing={2}
          gap={2}
          flexDirection={{ xs: 'column', sm: 'row' }}
          marginBottom={3}
          style={{
            width: 'fit-content',
          }}>
          <UploadButton
            disabled={isDisabled}
            accept='.csv'
            label='Import CSV File'
            multiple={false}
            uploadCallback={handleUploadCsvFile}
          />
          <DownloadButton
            disabled={isDisabled}
            label='Download Template'
            fileUrl='/resources/files/templates/tier-template.csv'
          />
        </Stack>
      </Show>
      <Box
        sx={{
          maxHeight: '420px',
          overflowY: 'auto',
          pr: 3
        }}>
        {!fields.length ? (
          <Box width={'100%'}>
            <Box
              width={'100%'}
              display={'flex'}
              justifyContent={'center'}
              paddingY={2}
              sx={{
                color: 'white',
                border: `1px solid ${errors.tier?.layer ? colors.error : colors.neutral015}`,
                borderRadius: '4px',
              }}>
              No tiers added
            </Box>
            {errors.tier?.layer ? (
              <Typography paddingTop={1} color={colors.error} fontSize={'14px'}>
                {errors.tier.layer.message}
              </Typography>
            ) : null}
          </Box>
        ) : (
          <>
            {fields.map((field, index) => (
              <TierItem
                onEdit={() => {
                  if (isDisabled) return false;
                  if (expandRows.includes(field.id)) {
                    setExpandRows(prev => prev.filter(i => i !== field.id));
                  } else {
                    setExpandRows(prev => [...prev, field.id]);
                  }
                }}
                index={index}
                isModifying={expandRows.includes(field.id)}
                key={field.id}
                onDelete={e => {
                  e.preventDefault();
                  if (isDisabled) return false;
                  remove(index);
                }}
                onSave={async () => {
                  if (isDisabled) return false;
                  const isValid = await trigger(`tier.layer.${index}`);
                  if (isValid) {
                    setExpandRows(prev => prev.filter(i => i !== field.id));
                  }
                }}
              />
            ))}
            {errors.tier?.layer ? (
              <Typography paddingTop={1} color={colors.error} fontSize={'14px'}>
                {errors.tier.layer.message}
              </Typography>
            ) : null}
          </>
        )}
      </Box>
      <Show when={!hasDeployed}>
        <Box marginTop={2}>
          <PrimaryButton
            isFullWidth
            disabled={isDisabled}
            startIcon={<AddIcon />}
            onClick={() => {
              if (isDisabled) return false;
              if (errors.tier?.layer) {
                clearErrors('tier.layer');
              }
              append({ price: 0, total: 0 });
            }}>
            Add Tier
          </PrimaryButton>
        </Box>
      </Show>
      <AutoPriceAdjustment isDisabled={isDisabled} />
    </Box>
  );
}
